import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { getSingleUserUseCase } from "@src/cloud-connect/user-manager/use-cases/get-single-user-use-case";
import { useI18N } from "@src/language/language-context";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { updateShowOtaAcceptAndPushFirmwareModal } from "@src/reducers/your-reducer";
import { OTA_OBJECT_TYPE } from "@src/cloud-connect/ota-update/constants";
import { cancelInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/cancel-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

interface UseOtaAcceptAndPushProps {
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

export const useOtaAcceptAndPush = ({
  activeInstallationData,
}: UseOtaAcceptAndPushProps) => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const [approvalUserName, setApprovalUserName] = useState<string | null>(null);
  const [isCancelRequestLoading, setIsCancelRequestLoading] = useState(false);

  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const isBackupInstallation =
    activeInstallationData?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const installationName = activeInstallationData?.firmware?.filename || "";

  const titleTemplate = isBackupInstallation
    ? i18n("modules.otaAcceptAndPush.titleTemplateBackup")
    : i18n("modules.otaAcceptAndPush.titleTemplateFirmware");

  const title = titleTemplate.replace("{itemName}", installationName);

  const itemNameMarker = "__ITEM_NAME__";
  const titleTemplateWithMarker = titleTemplate.replace(
    "{itemName}",
    itemNameMarker,
  );
  const [titlePrefix, titleSuffix = ""] =
    titleTemplateWithMarker.split(itemNameMarker);

  const texts = {
    title,
    titleHasItemName: titleTemplate.includes("{itemName}"),
    titlePrefix,
    titleSuffix,
    itemName: installationName,
    descriptionTemplate: isBackupInstallation
      ? i18n("modules.otaAcceptAndPush.descriptionTemplateBackup")
      : i18n("modules.otaAcceptAndPush.descriptionTemplateFirmware"),
    acceptAndPushButtonText: isBackupInstallation
      ? i18n("modules.otaAcceptAndPush.acceptAndPushButtonTextBackup")
      : i18n("modules.otaAcceptAndPush.acceptAndPushButtonTextFirmware"),
    cancelRequestButtonText: i18n(
      "modules.otaAcceptAndPush.cancelRequestButtonText",
    ),
    cancelSuccessMessage: i18n("modules.otaAcceptAndPush.cancelSuccessMessage"),
    cancelFailureMessage: i18n("modules.otaAcceptAndPush.cancelFailureMessage"),
    waitMessage: i18n("modules.otaAcceptAndPush.waitMessage"),
  };

  const handleAcceptAndPush = () => {
    dispatch(updateShowOtaAcceptAndPushFirmwareModal(true));
  };

  const handleCancelRequest = async () => {
    if (isCancelRequestLoading) {
      return;
    }

    const updateId = activeInstallationData?.update_id;
    if (!updateId) {
      return;
    }

    setIsCancelRequestLoading(true);

    try {
      const response = await cancelInstallationProcessUseCase(updateId);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from cancelInstallationProcessUseCase:",
          response,
          "for updateId:",
          updateId,
        );
      if (response.success) {
        successSnackbar(texts.cancelSuccessMessage, 5000);
        return;
      }

      errorSnackbar(texts.cancelFailureMessage, 5000);
    } catch {
      errorSnackbar(texts.cancelFailureMessage, 5000);
    } finally {
      setIsCancelRequestLoading(false);
    }
  };

  useEffect(() => {
    async function fetchApproverName() {
      if (!activeInstallationData?.approval_user_id) {
        setApprovalUserName("");
        return;
      }
      const response = await getSingleUserUseCase(
        activeInstallationData?.approval_user_id,
      );

      if (response.success && response.data?.display_name) {
        setApprovalUserName(response.data?.display_name);
      } else {
        setApprovalUserName("");
      }
    }

    fetchApproverName();
  }, [activeInstallationData?.approval_user_id]);

  const descriptionText = texts.descriptionTemplate.replace(
    "{approver}",
    approvalUserName || "",
  );

  return {
    texts,
    descriptionText,
    handleAcceptAndPush,
    handleCancelRequest,
    isCancelRequestLoading,
  };
};
